import { Modal } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "src/components/shared/Button/Button";
import { Select } from "src/components/shared/MantineCombobox";
import ScrollableTable from "src/components/shared/ScrollableTable/ScrollableTable";
import { IUser } from "src/components/shared/UserList/UserList.types";
import { Flex } from "src/styled";

import CrewListItem from "./CrewListItem";
const selectData = [
  {
    value: "*",
    label: "All",
  },
  {
    value: "1",
    label: "Active",
  },
];
const headers = [
  {
    name: "ref",
    label: "Ref",
    style: { width: "10%" },
  },
  {
    name: "full_name",
    label: "Name",
    style: { width: "50%" },
  },
  {
    name: "active",
    label: "Status",
    style: { width: "20%" },
  },
  {
    name: "blank",
    label: "",
    style: { width: "10%" },
  },
];
interface Props {
  opened: boolean;
  close: () => void;
  watch: (name: string) => any;
  setValue: (name: string, value: any) => void;
  allUsers: IUser[];
  multiple?: boolean;
  reportType?: string;
}
const ReportCrewSelectorModal = ({
  opened,
  close,
  watch,
  setValue,
  allUsers,
  multiple = true,
  reportType,
}: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sortBy = searchParams.get("sort");
  const sortDirection = searchParams.get("dir") === "desc" ? -1 : 1;
  const status: string = watch("data.crewStatus");
  const handleSetStatus = (value: string) => setValue("data.crewStatus", value);

  const reportTypes = [
    { value: "0", label: "Full Report" },
    { value: "2", label: "Event Report" },
    { value: "3", label: "Waste Declaration" },
    { value: "4", label: "Crew List Report" },
    { value: "5", label: "Confidential Medical Report" },
    { value: "6", label: "Crew Illness Report" },
    { value: "7", label: "Crew Sign On/Off Report" },
    { value: "8", label: "Hearing Tests Report" },
    { value: "9", label: "Vaccinations Report" },
  ];

  const getModalTitle = () => {
    return `Select Crew for ${reportTypes.find(rt => rt.value === reportType)?.label || "Report"}`;
  };

  const filteredUsers: IUser[] = useMemo(
    () =>
      allUsers.filter(
        (user: any) => status === "*" || user.active === Number(status),
      ),
    [allUsers, status],
  );
  const [sortedUsers, setSortedUsers] = useState<IUser[]>(filteredUsers);

  useEffect(() => {
    if (sortBy && headers.some(header => header.name === sortBy)) {
      setSortedUsers(
        [...filteredUsers].sort((a, b) => {
          const aValue = a[sortBy as keyof IUser];
          const bValue = b[sortBy as keyof IUser];
          if (typeof aValue === "string" && typeof bValue === "string") {
            if (Number.isNaN(Number(aValue)) && Number.isNaN(Number(bValue))) {
              return aValue.localeCompare(bValue) * sortDirection;
            } else {
              return (Number(aValue) - Number(bValue)) * sortDirection;
            }
          } else if (typeof aValue === "number" && typeof bValue === "number") {
            return (aValue - bValue) * sortDirection;
          }
          return 0;
        }),
      );
    } else {
      setSortedUsers(filteredUsers);
    }
  }, [sortBy, sortDirection, filteredUsers]);
  const handleSelectAll = () => {
    const newUsers = filteredUsers.map((u: IUser) => u.userid);
    setValue("data.selectedUsers", newUsers);
  };

  const handleClearAll = () => {
    setValue("data.selectedUsers", []);
  };

  const renderRow = (data: { row: IUser }) => (
    <CrewListItem
      user={data.row}
      watch={watch}
      setValue={setValue}
      multiple={multiple}
    />
  );
  const clearSort = () => {
    setSearchParams(prev => {
      prev.delete("sort");
      prev.delete("dir");
      return prev;
    });
  };
  return (
    <Modal title={getModalTitle()} opened={opened} onClose={close} size="xl">
      <div>
        <Flex justify="space-between" align="flex-end">
          <Flex gap={10} align="flex-end">
            <Select
              data={selectData}
              onChange={handleSetStatus}
              value={status}
              label="Status"
              w={200}
            />
            {sortBy && <Button onClick={clearSort}>Clear Sorting</Button>}
            {multiple && <Button onClick={handleSelectAll}>Select All</Button>}
            <Button color="red" onClick={handleClearAll}>
              {multiple ? "Clear All Selections" : "Clear Selection"}
            </Button>
          </Flex>
        </Flex>
        <ScrollableTable
          style={{ padding: 20, height: 500 }}
          data={sortedUsers}
          headers={headers}
          renderRow={renderRow}
          sortable
        />
        <Flex justify="flex-end">
          <Button onClick={close} variant="filled">
            Okay
          </Button>
        </Flex>
      </div>
    </Modal>
  );
};
export default ReportCrewSelectorModal;
